

import React, { useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useHistory, useLocation, useParams } from "react-router-dom";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import formatDate from "src/utils/formatDate";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import { BudgetType } from "../ConsultValues/interface";
import { BudgetLogsFilters } from "./interface";


const BudgetLogs: React.FC = () => {

	const params = useParams<{
    budgetPlanId: string;
  }>();

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const history = useHistory();

	const { translate, currentLanguage } = useTranslation();
	const { 
		handleSubmit : handleSubmitFilters, 
		reset: resetFilters ,
		setValue: setValueFilters,
		control: controlFilters
	} = useForm();

	const paginationRef = useRef<IForwardPagination>(null);

	const [financialPlanTitle] = useState(objQueryParams?.name || "");
	const [showFilters, setShowFilters] = useState(true);
	const [filters, setFilters] = useState<BudgetLogsFilters>({ 
		budget: undefined,
		dtStart: undefined,
		dtEnd: undefined
	});

	const optBudgetType = [{
		label: translate("Custos variáveis"),
		value: BudgetType.VARIABLES_COSTS
	},{
		label: translate("Dedução de vendas"),
		value: BudgetType.OPERATIONAL_EXPENSES
	},{
		label: translate("Despesas operacionais"),
		value: BudgetType.OPERATIONAL_EXPENSES
	},{
		label: translate("Despesas variáveis"),
		value: BudgetType.VARIABLES_EXPENSES
	},{
		label: translate("Outras despesas"),
		value: BudgetType.OTHER_EXPENSES
	},{
		label: translate("Receita de vendas"),
		value: BudgetType.SALES_REVENUE
	}];

	const submitFilters = (dataFilter: any) => {
		setFilters({
			budget: dataFilter.budget?.value || dataFilter.budget || "",
			dtStart: dataFilter.dtStart ?  formatDate(dataFilter.dtStart) : "",
			dtEnd: dataFilter.dtEnd ? formatDate(dataFilter.dtEnd) : ""
		});	
	};

	useEffectSkipInitialRender(() => {
		if(!filters.budget)
			return; 
		paginationRef.current?.reload();
	}, [filters]);

	return (
		<>
    	<PageTitle
				title={`${translate("Logs de orçamento")} - ${financialPlanTitle}`}
				icon={{
					name: "list"
				}}
			>
				<RCCTooltip
					text={translate("Filtros")}
					color={showFilters || filters.budget || filters.dtEnd ||  filters.dtStart ? "blue" : "grey"}
				>
					<RCCButton
						icon={{name: "filter", color: "#FFF", size: 16}}
						color={showFilters || filters.budget || filters.dtEnd ||  filters.dtStart ? "blue" : "grey"}
						layout="isRounded"
						size="md"
						onClick={() => setShowFilters((prevState) => !prevState)}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Voltar")}
					color="grey"
				>
					<RCCButton
						icon={{name: "arrowBack", color: "#FFF"}}
						color="grey"
						layout="isRounded"
						size="md"
						onClick={() => history.push(`/financialmanagement/panel/${params.budgetPlanId}`)}
					/>
				</RCCTooltip>
			</PageTitle>

			<FiltersContainer
				onSubmitFilters={handleSubmitFilters(submitFilters)}
				resetFilters={() => resetFilters({ 
					budget: "",
					dtStart: null,
					dtEnd: null
				})}
				showFilters={showFilters}
			>
				<RCCGrid
					container
					item
					spacing={2}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="budget"
							control={controlFilters}
							defaultValue={filters.budget ? String(filters.budget): ""}
							options={optBudgetType}
							label={translate("Orçamento")}
							submit="value and label"
							required
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<DateTimePickerRange
							control={controlFilters}
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit="dtStart"
							nameEnd="dtEnd"
							defaultValueEnd={filters.dtEnd}
							defaultValueInit={filters.dtStart}
							type="datePicker"
							setValue={setValueFilters}
						/>
					</RCCGrid>
				</RCCGrid>
			</FiltersContainer>
		
			{filters.budget ? (
				<Pagination 
					ref={paginationRef}
					dataOrRoute="FinancialManagement/ListLogs"
					filtersData={{
						...filters,
						budgetPlanId: params.budgetPlanId
					}}     
					columns={[
						{
							idColumn: "ofl001Datahora",
							nameColumn: translate("Data e hora"),
							xs: 3,
							sm: 3,
							md: 3,
							cellRenderer: (row: any) => `${formatDate(row.ofl001Datahora, currentLanguage, true)}`
						},
						{
							idColumn: "ofl001Mensagem",
							nameColumn: translate("Mensagem"),
							xs: 9,
							sm: 9,
							md: 9,
							type: "string",
							truncate: true,
							styleInline: "min-height: 35px;"
						}				
					]}
				/>
			) : (
				<RCCAlert
					description={translate("Selecione o orçamento acima para carregar os logs")}
				/>
			)}

		</>
	);
};

export default BudgetLogs;
